import { GapDraftAgentStatus, } from './gap-draft.domain.js';
/**
 * What a human reviewer may edit on a gap draft, and when.
 *
 * The list is a CONTRACT, not a convenience. A change request carries dotted
 * paths that become a Mongo `$set`, so an unvalidated path lets a caller write
 * `review.status`, `metadata.isDeleted` or into the frozen `source` layer. The
 * API validates against this list and the reader renders its edit mode from it,
 * which is also why it lives here rather than in either of them.
 */
/** How a field is edited, which decides the control the reader renders. */
export var GapDraftEditableKind;
(function (GapDraftEditableKind) {
    /** A single line — a title, a label, an ROI figure. */
    GapDraftEditableKind["TEXT"] = "text";
    /** Prose, possibly several paragraphs. */
    GapDraftEditableKind["MARKDOWN"] = "markdown";
    /** A 0–1 score. */
    GapDraftEditableKind["SCORE"] = "score";
})(GapDraftEditableKind || (GapDraftEditableKind = {}));
/**
 * Every editable path.
 *
 * Two kinds of field are absent on purpose. `category`, `externalRef` and
 * `translations` are machine identity — a reviewer rewriting them would break
 * the join to `canonical_records` or desynchronise a translation from its
 * original. And inside the lists, only the prose leaves are here: a timeline
 * event's `kind`, an evidence `ref` and a precedent's `id` are the parts the
 * pipeline keys on.
 *
 * `severity` and `missability` are editable like any other authored field: the
 * enrichment agent recomputes each from the record and writes its reasoning to
 * `GapDraft.severityReasoning` and `GapDraft.missabilityReasoning`, so a
 * reviewer reading a score and its argument together can disagree and correct
 * it. Both are among the four fields publishing a `Gap` requires (see the
 * readiness checklist), so being able to say so is not optional.
 */
export const EDITABLE_GAP_FIELDS = [
    { path: 'gap.title', kind: GapDraftEditableKind.TEXT, label: 'Title' },
    {
        path: 'gap.severity',
        kind: GapDraftEditableKind.SCORE,
        label: 'Severity',
    },
    {
        path: 'gap.missability',
        kind: GapDraftEditableKind.SCORE,
        label: 'Missability',
    },
    {
        path: 'gap.potentialImpact',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Potential impact',
    },
    {
        path: 'gap.fullProjectContext',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Full project context',
    },
    {
        path: 'gap.projectOverview.account',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Account narrative',
    },
    {
        path: 'gap.projectOverview.project',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Project narrative',
    },
    {
        path: 'gap.projectOverview.gapSummary',
        kind: GapDraftEditableKind.TEXT,
        label: 'Gap summary',
    },
    {
        path: 'gap.roiImpact.value',
        kind: GapDraftEditableKind.TEXT,
        label: 'ROI figure',
    },
    {
        path: 'gap.roiImpact.why',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'ROI rationale',
    },
    {
        path: 'gap.actionItems.intro',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Actions intro',
    },
    {
        path: 'gap.actionItems.items.*.text',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Action',
    },
    {
        path: 'gap.escalation.events.*.dateLabel',
        kind: GapDraftEditableKind.TEXT,
        label: 'Event date',
    },
    {
        path: 'gap.escalation.events.*.title',
        kind: GapDraftEditableKind.TEXT,
        label: 'Event title',
    },
    {
        path: 'gap.escalation.events.*.description',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Event description',
    },
    {
        path: 'gap.seenBefore.projects.*.name',
        kind: GapDraftEditableKind.TEXT,
        label: 'Precedent name',
    },
    {
        path: 'gap.seenBefore.projects.*.description',
        kind: GapDraftEditableKind.MARKDOWN,
        label: 'Precedent description',
    },
];
const EDITABLE_PATH_TEMPLATES = new Set(EDITABLE_GAP_FIELDS.map((editableField) => editableField.path));
/** `gap.actionItems.items.1.text` → `gap.actionItems.items.*.text`. */
export function toEditablePathTemplate(path) {
    return path
        .split('.')
        .map((segment) => (/^\d+$/.test(segment) ? '*' : segment))
        .join('.');
}
/**
 * Whether a path may be written by a human.
 *
 * Being in the list is necessary but not sufficient for an indexed path: the
 * index also has to be inside the array that is actually there, or `$set` on
 * `items.99.text` pads the array with nulls up to 99. Only the API can check
 * that, because only it has the document — see the service.
 */
export function isEditableGapPath(path) {
    return EDITABLE_PATH_TEMPLATES.has(toEditablePathTemplate(path));
}
/** The descriptor governing a concrete path, indexed or not. */
export function editableFieldOf(path) {
    const template = toEditablePathTemplate(path);
    return EDITABLE_GAP_FIELDS.find((editableField) => editableField.path === template);
}
/**
 * Whether AT LEAST ONE enrichment agent finished CLEANLY — the first half of the
 * gate on editing.
 *
 * The agents own disjoint sets of `gap.*` fields: `overview` writes the heading,
 * the overview and the timeline, `enrichment` writes the rest. So a FAILED
 * `overview` says nothing about the fields `enrichment` finished cleanly, and
 * requiring every agent to be DONE would lock a reviewer out of good prose
 * because an unrelated agent gave up — and would lock every existing draft the
 * moment another agent is added to the enum.
 *
 * What actually keeps a reviewer out of a field no agent wrote is
 * {@link isFinalGapValue}, the per-field half of the gate directly below: it
 * locks anything absent or blank. A draft only `overview` has run on therefore
 * exposes exactly the three fields `overview` wrote and nothing else. That is
 * the load-bearing argument for opening the draft on one DONE agent rather than
 * all of them.
 *
 * A missing entry means that agent has not started (there is no NOT_RUN status —
 * absence is how that is expressed), so a draft nothing has finished on stays
 * shut.
 */
export function isGapDraftEnriched(agents) {
    return Object.values(agents).some((entry) => entry?.status === GapDraftAgentStatus.DONE);
}
/**
 * Whether a value on `gap` is FINAL — the second half of the gate, applied per
 * field.
 *
 * An enriched draft still holds paths nothing ever wrote: `roiImpact` has no
 * agent behind it at all, and a prompt that skips an optional field leaves it
 * absent or blank. Those are not versions of anything, so there is nothing for a
 * reviewer to correct — the field stays locked until something produces a value
 * for it. A blank string counts as unwritten; `0` does not, since a zero score
 * is a real judgement.
 */
export function isFinalGapValue(value) {
    if (typeof value === 'number')
        return Number.isFinite(value);
    return typeof value === 'string' && value.trim().length > 0;
}
